import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Wraps React.lazy() with a one-time reload guard to handle ChunkLoadErrors.
 * When a stale cached index.html references chunk hashes that no longer exist
 * on the server after a deploy, this triggers a single hard reload to pick up
 * the fresh index.html with correct chunk URLs. The sessionStorage flag prevents
 * an infinite reload loop if the chunk is genuinely missing.
 */
// The generic parameter `P` is the component's props type, inferred from the
// imported module's default export. Using `ComponentType<P>` (rather than a
// bare `T extends ComponentType`) lets TS infer `P` for each call site so
// callers such as `lazyWithChunkRetry(() => import('./FooFC'))` end up with a
// `LazyExoticComponent<ComponentType<FooProps>>` rather than the empty-bottom
// `ComponentType<never>` produced by a broken contravariant unification.
export const lazyWithChunkRetry = <P = Record<string, unknown>>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  onRetry: () => void = () => window.location.reload()
): LazyExoticComponent<ComponentType<P>> => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      const STORAGE_KEY = 'chunk-refresh-attempted';
      if (sessionStorage.getItem(STORAGE_KEY) !== 'true') {
        sessionStorage.setItem(STORAGE_KEY, 'true');
        onRetry();
      }
      throw error;
    }
  });
};
